<template>
  <div class="morning-check">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <h3>{{ title || '今日晨检记录' }}</h3>
      <div v-if="!isOverview && stats.length" class="section-stats">
        <span v-for="(item, index) in stats" :key="index" class="stat-item">
          <span class="stat-label">{{ item.label }}</span>
          <span class="stat-value">{{ item.value }}</span>
        </span>
      </div>
      <span v-else-if="isOverview" class="rate-badge">完成率 {{ overview.rate || 0 }}%</span>
    </div>

    <div v-if="isOverview" class="overview">
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progressWidth }"></div>
      </div>

      <div class="overview-body">
        <div class="attendance">
          <div v-for="item in attendance" :key="item.key" class="bar-item">
            <span class="bar-count">{{ item.value }}人</span>
            <div class="bar-track">
              <div class="bar-fill" :class="item.key" :style="{ height: item.percent + '%' }"></div>
            </div>
            <span class="bar-label">{{ item.label }}</span>
          </div>
        </div>

        <div class="abnormal">
          <div class="abnormal-title">异常统计</div>
          <div v-for="item in abnormalList" :key="item.key" class="abnormal-item">
            <span class="abnormal-label" :class="item.key">{{ item.label }}</span>
            <span class="abnormal-dot"></span>
            <span class="abnormal-value">{{ item.value }}</span>
            <span class="abnormal-unit">次</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else ref="scrollContainer" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="header in headers" :key="header.field_name">{{ header.name }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(record, index) in [...records, ...records]"
            :key="index"
            :class="{ 'even': index % 2 === 0, 'odd': index % 2 !== 0 }"
            class="table-row"
          >
            <td
              v-for="header in headers"
              :key="header.field_name"
              :class="cellClass(record, header)"
            >
              {{ record[header.field_name] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MorningCheck',
  props: {
    title: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'table'
    },
    headers: {
      type: Array,
      default: () => []
    },
    records: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    },
    overview: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    isOverview() {
      return this.mode === 'overview'
    },
    progressWidth() {
      const rate = Math.min(Math.max(Number(this.overview.rate) || 0, 0), 100)
      return rate + '%'
    },
    attendance() {
      const expected = Number(this.overview.expected) || 0
      const actual = Number(this.overview.actual) || 0
      const max = Math.max(expected, actual, 1)
      return [
        { key: 'expected', label: '应到', value: expected, percent: Math.round(expected * 100 / max) },
        { key: 'actual', label: '实到', value: actual, percent: Math.round(actual * 100 / max) }
      ]
    },
    abnormalList() {
      const list = this.overview.abnormal
      if (Array.isArray(list) && list.length) {
        return list
      }
      return [
        { key: 'symptom', label: '症状检测', value: 0 },
        { key: 'temperature', label: '体温检测', value: 0 },
        { key: 'hand', label: '手部检测', value: 0 }
      ]
    }
  },
  methods: {
    cellClass(record, header) {
      if (header.field_name !== 'status') return ''
      return record[header.field_name] === '正常' ? 'pass' : 'fail'
    },
    startAutoScroll() {
      if (this.isOverview) return
      const container = this.$refs.scrollContainer
      if (!container) return

      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (scrollHeight <= clientHeight) return

      const halfHeight = scrollHeight / 2
      let scrollTop = 0

      const scroll = () => {
        scrollTop += 1
        if (scrollTop >= halfHeight) {
          container.scrollTop = 0
          scrollTop = 0
        }
        container.scrollTop = scrollTop
      }

      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(scroll, 30)
    }
  },
  beforeDestroy() {
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
    }
  }
}
</script>

<style scoped>
.morning-check {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.icon {
  width: 20px;
  height: 20px;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.section-stats {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-left: auto;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #a0aec0;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #63b3ed;
}

.rate-badge {
  margin-left: auto;
  font-size: 13px;
  color: #63b3ed;
  border: 1px solid rgba(99, 179, 237, 0.4);
  border-radius: 10px;
  padding: 2px 10px;
  white-space: nowrap;
}

/* ==================== 总览 ==================== */
.overview {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.progress-track {
  height: 10px;
  border-radius: 5px;
  background: rgba(99, 179, 237, 0.15);
  overflow: hidden;
  flex-shrink: 0;
}

.progress-fill {
  height: 100%;
  border-radius: 5px;
  background: linear-gradient(90deg, #2b6cb0, #63b3ed);
  box-shadow: 0 0 10px rgba(99, 179, 237, 0.6);
  transition: width 0.6s ease;
}

.overview-body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 20px;
  padding-top: 16px;
}

.attendance {
  flex: 0 0 42%;
  display: flex;
  justify-content: center;
  gap: 24px;
  min-height: 0;
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-height: 0;
}

.bar-count {
  font-size: 18px;
  font-weight: 700;
  color: #e2e8f0;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.bar-track {
  flex: 1;
  width: 34px;
  min-height: 20px;
  display: flex;
  align-items: flex-end;
  background: rgba(99, 179, 237, 0.12);
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  width: 100%;
  border-radius: 4px;
  min-height: 4px;
  transition: height 0.6s ease;
}

.bar-fill.expected {
  background: linear-gradient(180deg, #63b3ed, #2b6cb0);
  box-shadow: 0 0 10px rgba(99, 179, 237, 0.5);
}

.bar-fill.actual {
  background: linear-gradient(180deg, #68d391, #2f855a);
  box-shadow: 0 0 10px rgba(104, 211, 145, 0.5);
}

.bar-label {
  font-size: 13px;
  color: #a0aec0;
  margin-top: 8px;
  flex-shrink: 0;
}

.abnormal {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.abnormal-title {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
}

.abnormal-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed rgba(99, 179, 237, 0.12);
}

.abnormal-item:last-child {
  border-bottom: none;
}

.abnormal-label {
  flex: 0 0 76px;
  font-size: 14px;
  font-weight: 600;
}

.abnormal-label.symptom {
  color: #f6ad55;
}

.abnormal-label.temperature {
  color: #63b3ed;
}

.abnormal-label.hand {
  color: #68d391;
}

.abnormal-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56565;
  box-shadow: 0 0 8px rgba(245, 101, 101, 0.8);
}

.abnormal-value {
  font-size: 16px;
  font-weight: 700;
  color: #e2e8f0;
  min-width: 20px;
  text-align: right;
}

.abnormal-unit {
  font-size: 12px;
  color: #a0aec0;
}

/* ==================== 表格 ==================== */
.table-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  color: #a0aec0;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  position: sticky;
  top: 0;
  background: rgba(26, 54, 93, 0.9);
}

.data-table td {
  padding: 8px 10px;
  font-size: 12px;
  color: #e2e8f0;
  transition: all 0.3s ease;
}

.table-row {
  animation: slideIn 0.3s ease-out;
}

.table-row.even {
  background: rgba(49, 130, 206, 0.1);
}

.table-row.odd {
  background: rgba(49, 130, 206, 0.05);
}

.table-row:hover {
  background: rgba(49, 130, 206, 0.2);
}

.pass {
  color: #48bb78;
}

.fail {
  color: #fc8181;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
